import { Firestore, doc, getDoc, collection, addDoc } from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import { Timestamp, updateDoc } from 'firebase/firestore';

/**
 * Updates metadata for a media item
 */
export async function updateMediaMetadata(
    firestore: Firestore,
    mediaId: string,
    updates: {
        title?: string;
        description?: string;
        tags?: string[];
        aircraftModelIds?: string[];
        aircraftModelNames?: string[];
        isPanorama?: boolean;
    }
): Promise<void> {
    const mediaRef = doc(firestore, 'media', mediaId);
    // We explicitly only allow updating these specific fields
    await import('firebase/firestore').then(m =>
        m.updateDoc(mediaRef, {
            ...updates,
            updatedAt: m.Timestamp.now()
        })
    );
}

/**
 * Deletes a media item via server API
 * This also handles cache invalidation and album updates
 */
export async function deleteMediaItem(
    firestore: Firestore,
    mediaId: string
): Promise<void> {
    const auth = getAuth();
    const user = auth.currentUser;

    if (!user) {
        throw new Error('You must be logged in to delete media');
    }

    const token = await user.getIdToken();

    const response = await fetch('/api/gallery/delete-media', {
        method: 'DELETE',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ mediaId }),
    });

    if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to delete media');
    }
}

/**
 * Moves a media item to a different album
 */
export async function moveMediaItem(
    firestore: Firestore,
    mediaId: string,
    targetAlbumId: string,
    targetAlbumPath: string
): Promise<void> {
    const auth = getAuth();
    const user = auth.currentUser;

    if (!user) {
        throw new Error('You must be logged in to move media');
    }

    const mediaRef = doc(firestore, 'media', mediaId);
    const mediaSnap = await getDoc(mediaRef);

    if (!mediaSnap.exists()) {
        throw new Error('Media item not found');
    }

    const oldAlbumId = mediaSnap.data().albumId;

    // Update the media item's album reference
    await updateDoc(mediaRef, {
        albumId: targetAlbumId,
        albumPath: targetAlbumPath,
        updatedAt: Timestamp.now()
    });

    // Update media counts on both albums
    await updateAlbumMediaCount(firestore, oldAlbumId, -1);
    await updateAlbumMediaCount(firestore, targetAlbumId, 1);
}

/**
 * Copies a media item to a different album
 */
export async function copyMediaItem(
    firestore: Firestore,
    mediaId: string,
    targetAlbumId: string,
    targetAlbumPath: string
): Promise<string> {
    const auth = getAuth();
    const user = auth.currentUser;

    if (!user) {
        throw new Error('You must be logged in to copy media');
    }

    const mediaRef = doc(firestore, 'media', mediaId);
    const mediaSnap = await getDoc(mediaRef);

    if (!mediaSnap.exists()) {
        throw new Error('Media item not found');
    }

    const originalData = mediaSnap.data();

    // Create a new media document with the same data but different album
    const mediaCollection = collection(firestore, 'media');
    const newMediaRef = await addDoc(mediaCollection, {
        ...originalData,
        albumId: targetAlbumId,
        albumPath: targetAlbumPath,
        copiedFrom: mediaId,
        uploadedAt: Timestamp.now(),
        updatedAt: Timestamp.now()
    });

    // Update media count on target album
    await updateAlbumMediaCount(firestore, targetAlbumId, 1);

    return newMediaRef.id;
}

/**
 * Helper to update album media count
 */
async function updateAlbumMediaCount(
    firestore: Firestore,
    albumId: string,
    delta: number
): Promise<void> {
    const albumRef = doc(firestore, 'albums', albumId);
    const albumSnap = await getDoc(albumRef);

    if (albumSnap.exists()) {
        const currentCount = albumSnap.data().mediaCount || 0;
        await updateDoc(albumRef, {
            mediaCount: Math.max(0, currentCount + delta),
            updatedAt: Timestamp.now()
        });
    }
}
